import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

// Minimum gap kept between a clamped tooltip and the edge it is clamped to.
const CLIP_MARGIN = 8;

// The horizontal bounds a tooltip must stay inside: the viewport, narrowed by any scrolling or
// otherwise-clipping ancestor. Uses clientWidth throughout, since offsetWidth/innerWidth include
// the scrollbar and a tooltip parked under a scrollbar is still visibly cut off.
// Note: getBoundingClientRect() is post-transform while clientWidth is layout px. That matches
// today because no ancestor is scaled; a scaled/zoomed ancestor would skew these bounds.
const getClipBounds = (node) => {
  let left = 0;
  let right = document.documentElement.clientWidth;
  for (let el = node.parentElement; el && el !== document.documentElement; el = el.parentElement) {
    const style = getComputedStyle(el);
    if (style.overflowX === 'visible' && style.overflowY === 'visible') continue;
    const box = el.getBoundingClientRect();
    const innerLeft = box.left + (parseFloat(style.borderLeftWidth) || 0);
    left = Math.max(left, innerLeft);
    right = Math.min(right, innerLeft + el.clientWidth);
  }
  return { left, right };
};

const getPositionClasses = (position, anchor, showArrow, size) => {
  let positionClasses = [];
  if (position === 'left') {
    // Position to the left of the element, vertically centered
    positionClasses.push('right-full top-1/2 -translate-y-1/2');
    if (showArrow) {
      positionClasses.push('mr-1');
    } else {
      positionClasses.push('mr-0.5');
    }
    return positionClasses.join(' ');
  }

  if (position === 'right') {
    // Position to the right of the element, vertically centered
    positionClasses.push('left-full top-1/2 -translate-y-1/2');
    if (showArrow) {
      positionClasses.push('ml-1');
    } else {
      positionClasses.push('ml-0.5');
    }
    return positionClasses.join(' ');
  }

  if (position === 'top') {
    if (showArrow) {
      positionClasses.push('-top-9');
    } else {
      positionClasses.push('-top-7');
    }
  } else if (position === 'bottom') {
    if (showArrow) {
      positionClasses.push(size === 'xs' ? '-bottom-8' : '-bottom-9');
    } else {
      positionClasses.push(size === 'xs' ? '-bottom-6' : '-bottom-7');
    }
  }

  if (anchor === 'left') {
    positionClasses.push('left-0');
  } else if (anchor === 'right') {
    positionClasses.push('-right-2');
  } else if (anchor === 'center') {
    positionClasses.push('left-1/2 -translate-x-1/2');
  }
  return positionClasses.join(' ');
};

const getArrowPositionClasses = (position, anchor, maxWidth) => {
  let positionClasses = [];
  if (position === 'left') {
    // Arrow on the right edge, vertically centered
    positionClasses.push(maxWidth ? '-right-[11px]' : '-right-[7px]');
    positionClasses.push('top-1/2 -translate-y-1/2');
    return positionClasses.join(' ');
  }

  if (position === 'right') {
    // Arrow on the left edge, vertically centered
    positionClasses.push(maxWidth ? '-left-[11px]' : '-left-[7px]');
    positionClasses.push('top-1/2 -translate-y-1/2');
    return positionClasses.join(' ');
  }

  if (position === 'top') {
    positionClasses.push('-bottom-[7px]');
  } else if (position === 'bottom') {
    positionClasses.push('-top-[7px]');
  }

  if (anchor === 'left') {
    positionClasses.push('left-0');
  } else if (anchor === 'right') {
    positionClasses.push('right-1');
  } else if (anchor === 'center') {
    positionClasses.push('left-1/2 -translate-x-1/2');
  }
  return positionClasses.join(' ');
};

export const WithTooltip = ({
  children,
  text,
  showArrow,
  disabled,
  position = 'bottom',
  anchor = 'left',
  closeOnClick = false,
  forceShow = false,
  maxWidth,
  zIndex,
  size = 'sm',
  usePortal = false,
  offset = 0,
  className = '',
}) => {
  const [shown, setShown] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const wrapperRef = useRef(null);
  const [portalPos, setPortalPos] = useState(null);
  const tooltipRef = useRef(null);
  const [clampShift, setClampShift] = useState(0);

  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/android/i.test(userAgent) || /iPad|iPhone|iPod/.test(userAgent)) {
      setIsMobileDevice(true);
    }
  }, []);

  useEffect(() => {
    if (disabled) setShown(false);
  }, [disabled]);

  const showTooltip = (forceShow || (shown && !isMobileDevice)) && text;

  const positionClasses = getPositionClasses(position, anchor, showArrow, size);
  const arrowPositionClasses = getArrowPositionClasses(position, anchor, maxWidth);

  // Placement above is class-driven, so a tooltip on an edge-most trigger can run past its
  // clipping edge (nothing here measures). Once shown, slide it back inside and give the arrow an
  // equal counter-shift so it keeps pointing at the trigger. No-op when it already fits.
  const isVerticalPlacement = position === 'top' || position === 'bottom';
  useLayoutEffect(() => {
    if (!isVerticalPlacement || !showTooltip || !tooltipRef.current) return;
    const rect = tooltipRef.current.getBoundingClientRect();
    // Back out any shift already applied so we always measure the natural position. A translate
    // does not affect width, so rect.width is the natural width either way.
    const naturalLeft = rect.left - clampShift;
    const bounds = getClipBounds(tooltipRef.current);
    const slack = bounds.right - bounds.left - rect.width;
    let next = 0;
    // A tooltip wider than the space it is clipped to cannot be rescued by shifting — correcting
    // one edge would only push it off the other. Leave those exactly where they were. Otherwise
    // honour CLIP_MARGIN, shrinking it to half the slack when there isn't room for the full margin
    // on both sides, so a near-exact fit still gets corrected instead of staying clipped.
    if (slack >= 0) {
      const margin = Math.min(CLIP_MARGIN, slack / 2);
      const minLeft = bounds.left + margin;
      const maxLeft = bounds.right - rect.width - margin;
      // Whole pixels keep text off subpixel boundaries and make the convergence check below exact.
      next = Math.round(Math.min(Math.max(naturalLeft, minLeft), maxLeft) - naturalLeft);
    }
    if (next !== clampShift) setClampShift(next);
  }, [
    isVerticalPlacement,
    showTooltip,
    clampShift,
    portalPos,
    text,
    position,
    anchor,
    maxWidth,
    size,
  ]);

  // Inline transforms must restate the translate that the position classes would apply,
  // since an inline transform replaces the class one outright.
  const baseTranslate =
    position === 'left' || position === 'right'
      ? 'translateY(-50%)'
      : anchor === 'center'
        ? 'translateX(-50%)'
        : '';
  const tooltipTransform = [baseTranslate, clampShift ? `translateX(${clampShift}px)` : '']
    .filter(Boolean)
    .join(' ');
  const arrowBaseTranslate =
    position === 'left' || position === 'right'
      ? 'translateY(-50%)'
      : anchor === 'center' || (usePortal && isVerticalPlacement)
        ? 'translateX(-50%)'
        : '';
  const arrowTransform = [arrowBaseTranslate, clampShift ? `translateX(${-clampShift}px)` : '']
    .filter(Boolean)
    .join(' ');

  const updatePortalPos = useCallback(() => {
    if (!usePortal || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const gap = showArrow ? 6 : 4;
    const additionalSpacing = gap + offset;
    const positionAxis = position === 'right' || position === 'left' ? 'horizontal' : 'vertical';
    const pos = {};

    if (positionAxis === 'horizontal') {
      // Horizontal placement: sit beside the trigger, vertically centered on it
      // (paired with a translateY(-50%) transform on the portal node below).
      pos.top = rect.top + rect.height / 2;
      if (position === 'right') {
        pos.left = rect.right + additionalSpacing;
      } else if (position === 'left') {
        pos.right = window.innerWidth - rect.left + additionalSpacing;
      }
    } else if (positionAxis === 'vertical') {
      if (position === 'bottom') {
        pos.top = rect.bottom + additionalSpacing;
      } else if (position === 'top') {
        pos.bottom = window.innerHeight - rect.top + additionalSpacing;
      }
      if (anchor === 'center') {
        pos.left = rect.left + rect.width / 2;
      } else if (anchor === 'right') {
        pos.right = window.innerWidth - rect.right;
      } else {
        pos.left = rect.left;
      }
    }
    setPortalPos(pos);
  }, [usePortal, position, anchor, showArrow, offset]);

  const handleMouseEnter = () => {
    updatePortalPos();
    setShown(true);
  };

  useEffect(() => {
    if (!usePortal || !shown) return;
    window.addEventListener('scroll', updatePortalPos, true);
    return () => window.removeEventListener('scroll', updatePortalPos, true);
  }, [usePortal, shown, updatePortalPos]);

  useEffect(() => {
    if (closeOnClick) {
      const handleClick = () => {
        setShown(false);
      };
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [closeOnClick]);

  const tooltipContent = (
    <>
      {text}
      {showArrow && (
        <div
          style={arrowTransform ? { transform: arrowTransform } : undefined}
          className={`absolute ${
            usePortal && (position === 'bottom' || position === 'top')
              ? position === 'bottom'
                ? '-top-[7px] left-1/2 -translate-x-1/2'
                : '-bottom-[7px] left-1/2 -translate-x-1/2'
              : arrowPositionClasses
          }`}>
          {position === 'right' ? (
            <svg
              width={maxWidth ? '14' : '10'}
              height='24'
              viewBox={maxWidth ? '0 0 14 24' : '0 0 10 24'}
              fill='none'
              xmlns='http://www.w3.org/2000/svg'>
              <path
                d={
                  maxWidth
                    ? 'M14 0 Q14 2 12.5 4 Q12 5 11 6 L2 10 Q0 12 0 12 Q0 12 2 14 L11 18 Q12 19 12.5 20 Q14 22 14 24'
                    : 'M10 0 Q10 2 8.5 4 Q8 5 7 6 L1.5 10 Q0 12 0 12 Q0 12 1.5 14 L7 18 Q8 19 8.5 20 Q10 22 10 24'
                }
                fill='#374151'
              />
            </svg>
          ) : position === 'left' ? (
            <svg
              width={maxWidth ? '14' : '10'}
              height='24'
              viewBox={maxWidth ? '0 0 14 24' : '0 0 10 24'}
              fill='none'
              xmlns='http://www.w3.org/2000/svg'>
              <path
                d={
                  maxWidth
                    ? 'M0 0 Q0 2 1.5 4 Q2 5 3 6 L12 10 Q14 12 14 12 Q14 12 12 14 L3 18 Q2 19 1.5 20 Q0 22 0 24'
                    : 'M0 0 Q0 2 1.5 4 Q2 5 3 6 L8.5 10 Q10 12 10 12 Q10 12 8.5 14 L3 18 Q2 19 1.5 20 Q0 22 0 24'
                }
                fill='#374151'
              />
            </svg>
          ) : (
            <svg
              width='24'
              height='10'
              className={`${position === 'bottom' ? 'rotate-180' : ''}`}
              viewBox='0 0 24 10'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M0 0 Q2 0 4 1.5 Q5 2 6 3 L10 8.5 Q12 10 12 10 Q12 10 14 8.5 L18 3 Q19 2 20 1.5 Q22 0 24 0'
                fill='#374151'
              />
            </svg>
          )}
        </div>
      )}
    </>
  );

  const tooltipClasses = `${maxWidth ? `whitespace-normal text-center ${size === 'xs' ? 'text-xs' : 'text-sm'} px-3 py-2` : `w-fit whitespace-nowrap ${size === 'xs' ? 'text-xs' : 'text-sm'} px-2 py-1`} bg-gray-700 text-white rounded-lg pointer-events-none font-normal print:hidden`;

  return (
    <span
      ref={wrapperRef}
      className={`group/tooltip relative flex ${className}`}
      onMouseEnter={usePortal ? handleMouseEnter : () => setShown(true)}
      onMouseLeave={() => setShown(false)}>
      {children}
      {!disabled &&
        usePortal &&
        portalPos &&
        text &&
        createPortal(
          <div
            ref={tooltipRef}
            className={`fixed ${zIndex == null ? 'z-[11] ' : ''}${tooltipClasses} transition-opacity duration-200 ${showTooltip ? 'opacity-100' : 'opacity-0'}`}
            style={{
              ...portalPos,
              ...(maxWidth ? { width: maxWidth } : {}),
              ...(zIndex != null ? { zIndex } : {}),
              ...(tooltipTransform ? { transform: tooltipTransform } : {}),
            }}>
            {tooltipContent}
          </div>,
          document.body
        )}
      {!disabled && !usePortal && showTooltip && (
        <div
          ref={tooltipRef}
          style={{
            ...(maxWidth ? { width: maxWidth } : {}),
            ...(zIndex != null ? { zIndex } : {}),
            ...(tooltipTransform ? { transform: tooltipTransform } : {}),
          }}
          className={`group-hover/tooltip:opacity-100 group-hover/tooltipExcluded:opacity-0 transition-opacity duration-200 opacity-100 ${tooltipClasses} absolute ${positionClasses} ${zIndex == null ? 'z-[99999]' : ''}`}>
          {tooltipContent}
        </div>
      )}
    </span>
  );
};
